/**
 * CampaignCommanderWizard — Sprint 2 · Project Symphony
 *
 * Conversational intake. Six answers, one at a time, with warm
 * acknowledgments between each. Ends with the Promise Sentence.
 *
 * Product Principles compliance:
 *   §1 Speak business, not AI — no LLM / provider vocabulary anywhere.
 *   §2 Recommend before asking — channels pre-selected from goal.
 *   §3 Remove decisions when confident — audience is optional-text
 *       (no drop-down of made-up segments).
 *   §5 Every screen answers "what next?" — the visible active question
 *       IS the next action.
 *   §7 Language is a feature — no "Submit", no "Continue". The wizard
 *       ends with a promise.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";

import { fetchBlueprint, submitCommander, updateBlueprintAnswers } from "../api";
import { useCampaignWizardDraft } from "./useCampaignWizardDraft";
import {
  ACKNOWLEDGMENTS,
  GOAL_CHANNEL_HINTS,
  type BusinessGoal,
  type CampaignDuration,
  type CampaignIntensity,
  type CommanderAnswers,
  type CommanderChannel,
  type CommanderResponse,
} from "../types";

// ── Copy (Product Principles Article III lives here) ─────────────────
const GREETING = "Good morning. Let's build your next campaign.";
const FINAL_PROMPT_MICROCOPY = "I've got what I need for that part.";

// ── UI helpers ────────────────────────────────────────────────────────
const GOAL_LABELS: Record<BusinessGoal, string> = {
  leads: "Generate qualified leads",
  sales: "Drive sales",
  awareness: "Grow awareness",
  appointments: "Book appointments",
  website_traffic: "Send people to the website",
  customer_retention: "Bring back existing customers",
};

const DURATION_LABELS: Record<CampaignDuration, string> = {
  two_weeks: "2 weeks",
  thirty_days: "30 days",
  ninety_days: "90 days",
  six_months: "6 months",
  twelve_months: "12 months",
};

const INTENSITY_LABELS: Record<CampaignIntensity, { title: string; note: string }> = {
  light: { title: "Light", note: "One touchpoint a week — a gentle presence." },
  standard: { title: "Standard", note: "Three posts a week — a balanced rhythm." },
  aggressive: { title: "Aggressive", note: "Five posts a week plus email — an active push." },
  dominate: { title: "Dominate", note: "Daily posts + weekly email — a full campaign sprint." },
};

const CHANNEL_LABELS: Record<CommanderChannel, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  x: "X",
  email: "Email",
  blog: "Blog",
  google_business_profile: "Google Business",
  ads_meta: "Meta Ads",
  ads_google: "Google Ads",
  sms: "SMS",
};

type StepIndex = 0 | 1 | 2 | 3 | 4 | 5;

// ══════════════════════════════════════════════════════════════════════
export function CampaignCommanderWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Patch 37.4.39 · when ``?resume={id}`` is present we open the wizard
  // in EDIT mode against that specific blueprint. localStorage key is
  // isolated from the "new campaign" draft so both can coexist.
  const editingBlueprintId = searchParams.get("resume");
  const isEditMode = editingBlueprintId != null;
  // Patch 34.2.2g · workspace-scoped draft persistence so refresh /
  // browser Back / token refresh never lose in-progress answers.
  // Patch 37.4.39 · edit-mode isolation of the draft key.
  const { draft, persist, clear: clearDraft, hydrateFromBlueprint } =
    useCampaignWizardDraft(editingBlueprintId);
  const [step, setStep] = useState<StepIndex>(draft.step as StepIndex);
  const [ack, setAck] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Partial<CommanderAnswers>>(
    draft.answers.channels ? draft.answers : { channels: [] },
  );
  const usedAcksRef = useRef<Set<string>>(new Set());
  const [finalResponse, setFinalResponse] = useState<CommanderResponse | null>(null);

  // Patch 37.4.39 · when the URL asks us to resume an existing
  // blueprint, fetch it once and rehydrate the local draft (only if
  // the local draft is empty — never clobber user-typed edits).
  const resumeQuery = useQuery({
    queryKey: ["campaign-blueprint-resume", editingBlueprintId],
    queryFn: () => fetchBlueprint(editingBlueprintId!),
    enabled: isEditMode,
    retry: false,
    staleTime: Infinity,
  });
  const hydrateOnceRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !resumeQuery.data || hydrateOnceRef.current) return;
    hydrateOnceRef.current = true;
    const bp = resumeQuery.data.blueprint;
    if (bp.status === "archived" || bp.status === "approved"
        || bp.status === "executing" || bp.status === "completed") {
      toast.error("This campaign can't be edited from its current state.");
      navigate(`/campaigns/${bp.id}/room`, { replace: true });
      return;
    }
    // Rehydrate only if the local draft is empty (protect user's
    // in-flight edits).
    if (!draft.answers.promotion) {
      hydrateFromBlueprint(bp.commander_answers);
      setAnswers(bp.commander_answers);
      setStep(5);
    }
  }, [isEditMode, resumeQuery.data, draft.answers.promotion,
      hydrateFromBlueprint, navigate]);

  // Autosave every meaningful state change (step + answers).
  useEffect(() => {
    persist({ step, answers });
  }, [step, answers, persist]);

  // When the persisted draft changes underneath us — workspace switch,
  // another tab clearing the draft — re-hydrate the local state.
  useEffect(() => {
    setStep(draft.step as StepIndex);
    setAnswers(draft.answers.channels ? draft.answers : { channels: [] });
    // Intentional: react to draft key changes, not to every persist call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.updatedAt === new Date(0).toISOString() ? "empty" : "loaded"]);

  // Patch 37.4.6 · surface backend errors instead of silently swallowing.
  // Previously the mutation only had ``onSuccess`` — a 402 "active
  // campaign cap exceeded" (or any other rejection) briefly rendered
  // the pending screen, then dropped the founder back into the
  // ChannelsStep with no explanation. From their perspective the Plan
  // button "did nothing". We now capture the error and let the render
  // branch surface it inline with a specific CTA when the cause is
  // known.
  const [submitError, setSubmitError] = useState<null | {
    code: string;
    message: string;
    status: number;
  }>(null);
  const mutation = useMutation({
    // Patch 37.4.39 · edit mode calls PATCH /answers on the SAME
    // blueprint id (no new row created); create mode calls the
    // Commander POST. localStorage draft is cleared on SUCCESS only —
    // a failed mutation leaves the founder's typing intact.
    mutationFn: async (a: CommanderAnswers): Promise<CommanderResponse> => {
      if (isEditMode && editingBlueprintId) {
        const { blueprint } = await updateBlueprintAnswers(editingBlueprintId, a);
        return {
          blueprint: blueprint as unknown as CommanderResponse["blueprint"],
          promise: "I've updated your campaign setup. Let's rebuild your marketing strategy.",
          next_step_url: `/campaigns/${blueprint.id}/strategy`,
        };
      }
      return submitCommander(a);
    },
    onMutate: () => setSubmitError(null),
    onSuccess: (r) => {
      setFinalResponse(r);
      // Successful submission clears the draft — nothing to restore
      // next time the founder opens the wizard.
      clearDraft();
    },
    onError: (err: unknown) => {
      const apiErr = err instanceof ApiError ? err : null;
      const code = apiErr?.code ?? "unknown_error";
      const message =
        apiErr?.message
        ?? (err instanceof Error ? err.message : "Something went wrong while planning your campaign. Please try again.");
      const status = apiErr?.status ?? 0;
      setSubmitError({ code, message, status });
      // Always surface a toast too so a founder on a small screen who
      // scrolls past the inline banner still knows something failed.
      toast.error(message);
    },
  });

  const startOver = () => {
    // Guarded on the button — confirm() is intentional (the wizard has
    // no in-app modal system beyond sonner toasts, and we specifically
    // want a synchronous "are you sure" gate).
    if (!window.confirm("Start over? This will clear all campaign answers.")) return;
    clearDraft();
    setStep(0);
    setAnswers({ channels: [] });
  };

  const goToNext = (nextStep: StepIndex) => {
    // Draw a warm acknowledgment we haven't used yet this session.
    const remaining = ACKNOWLEDGMENTS.filter((a) => !usedAcksRef.current.has(a));
    const phrase = remaining[Math.floor(Math.random() * remaining.length)] ?? "Good.";
    usedAcksRef.current.add(phrase);
    setAck(phrase);
    // Brief warmth pause — never a fake spinner.
    window.setTimeout(() => {
      setAck(null);
      setStep(nextStep);
    }, 750);
  };

  const suggestedChannels = useMemo<CommanderChannel[]>(() => {
    if (!answers.business_goal) return ["instagram", "facebook", "email"];
    return GOAL_CHANNEL_HINTS[answers.business_goal];
  }, [answers.business_goal]);

  // Patch 37.4.39 · when resuming an existing blueprint (or a
  // persisted draft that already reached step 5), the founder's
  // channel selection wins over the goal defaults. Otherwise a
  // "no-op resume" would silently rewrite channels back to
  // GOAL_CHANNEL_HINTS — the exact P0 defect the patch is meant to
  // solve. Empty [] means "no channels stored yet, use the hints".
  const initialChannels = useMemo<CommanderChannel[]>(
    () =>
      answers.channels && answers.channels.length > 0
        ? answers.channels
        : suggestedChannels,
    [answers.channels, suggestedChannels],
  );

  // Once step 5 answered, auto-submit. No "Continue" button. Ever.
  const handleFinish = (channels: CommanderChannel[]) => {
    const complete: CommanderAnswers = {
      promotion: answers.promotion!,
      business_goal: answers.business_goal!,
      target_audience: answers.target_audience!,
      duration: answers.duration!,
      intensity: answers.intensity!,
      channels,
    };
    setAnswers(complete);
    setAck(FINAL_PROMPT_MICROCOPY);
    mutation.mutate(complete);
  };

  // ── Final screen — the Promise ─────────────────────────────────────
  if (mutation.isPending || finalResponse) {
    return <PromiseScreen response={finalResponse} pending={mutation.isPending} />;
  }

  return (
    <div
      className="mx-auto max-w-2xl px-4 pt-16 pb-24 sm:pt-24"
      data-testid="campaign-commander"
    >
      {step === 0 && (
        <Greeting text={GREETING} />
      )}

      {/* Answer summary so the user always sees what they've committed to. */}
      {step > 0 && <AnswerSummary answers={answers} />}

      {/* Patch 34.2.2g · Start Over control — only shown once the
          founder has committed at least one answer, so an empty
          wizard doesn't offer a "clear" button pointlessly. */}
      {step > 0 && (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={startOver}
            data-testid="commander-start-over"
            className="text-xs text-muted-foreground underline hover:text-foreground"
          >
            Start over
          </button>
        </div>
      )}

      {/* Warm acknowledgment shown briefly between steps. */}
      {ack && (
        <p
          className="text-sm text-muted-foreground italic mb-8"
          data-testid="commander-acknowledgment"
        >
          {ack}
        </p>
      )}

      {step === 0 && (
        <PromotionStep
          onNext={(promotion) => {
            setAnswers((s) => ({ ...s, promotion }));
            goToNext(1);
          }}
        />
      )}
      {step === 1 && (
        <AudienceStep
          onNext={(target_audience) => {
            setAnswers((s) => ({ ...s, target_audience }));
            goToNext(2);
          }}
        />
      )}
      {step === 2 && (
        <GoalStep
          onNext={(business_goal) => {
            setAnswers((s) => ({ ...s, business_goal }));
            goToNext(3);
          }}
        />
      )}
      {step === 3 && (
        <DurationStep
          onNext={(duration) => {
            setAnswers((s) => ({ ...s, duration }));
            goToNext(4);
          }}
        />
      )}
      {step === 4 && (
        <IntensityStep
          onNext={(intensity) => {
            setAnswers((s) => ({ ...s, intensity }));
            goToNext(5);
          }}
        />
      )}
      {step === 5 && (
        <>
          {submitError ? (
            <CommanderErrorBanner
              error={submitError}
              onDismiss={() => setSubmitError(null)}
              onManageCampaigns={() => navigate("/campaigns")}
            />
          ) : null}
          <ChannelsStep
            key={initialChannels.join(",")}
            suggested={initialChannels}
            onFinish={handleFinish}
          />
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Steps                                                                 
// ══════════════════════════════════════════════════════════════════════

function Greeting({ text }: { text: string }) {
  return (
    <p
      className="text-3xl sm:text-4xl font-serif tracking-tight mb-10 leading-snug"
      data-testid="commander-greeting"
    >
      {text}
    </p>
  );
}

function Question({ children }: { children: React.ReactNode }) {
  return <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6">{children}</h2>;
}

function PromotionStep({ onNext }: { onNext: (v: string) => void }) {
  const [value, setValue] = useState("");
  const canAdvance = value.trim().length >= 2 && value.trim().length <= 240;
  return (
    <div data-testid="commander-step-promotion">
      <Question>What are we promoting today?</Question>
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Fall Dallisgrass Program"
        maxLength={240}
        aria-label="What are we promoting"
        onKeyDown={(e) => {
          if (e.key === "Enter" && canAdvance) onNext(value.trim());
        }}
        data-testid="commander-input-promotion"
      />
      <div className="mt-4">
        <Button
          disabled={!canAdvance}
          onClick={() => onNext(value.trim())}
          data-testid="commander-next-promotion"
        >
          That's it
        </Button>
      </div>
    </div>
  );
}

function AudienceStep({ onNext }: { onNext: (v: string) => void }) {
  const [value, setValue] = useState("");
  const canAdvance = value.trim().length >= 10 && value.trim().length <= 500;
  return (
    <div data-testid="commander-step-audience">
      <Question>Who's the ideal customer?</Question>
      <Textarea
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Homeowners with established lawns within 15 miles of Orlando."
        maxLength={500}
        rows={3}
        aria-label="Target audience"
        data-testid="commander-input-audience"
      />
      <div className="mt-4">
        <Button
          disabled={!canAdvance}
          onClick={() => onNext(value.trim())}
          data-testid="commander-next-audience"
        >
          That's them
        </Button>
      </div>
    </div>
  );
}

function GoalStep({ onNext }: { onNext: (v: BusinessGoal) => void }) {
  return (
    <div data-testid="commander-step-goal">
      <Question>What's the outcome you're hoping for?</Question>
      <div className="grid gap-3 sm:grid-cols-2">
        {(Object.keys(GOAL_LABELS) as BusinessGoal[]).map((g) => (
          <RadioCard
            key={g}
            testid={`commander-goal-${g}`}
            label={GOAL_LABELS[g]}
            onSelect={() => onNext(g)}
          />
        ))}
      </div>
    </div>
  );
}

function DurationStep({ onNext }: { onNext: (v: CampaignDuration) => void }) {
  return (
    <div data-testid="commander-step-duration">
      <Question>How long should I manage this campaign?</Question>
      <div className="grid gap-3 sm:grid-cols-3">
        {(Object.keys(DURATION_LABELS) as CampaignDuration[]).map((d) => (
          <RadioCard
            key={d}
            testid={`commander-duration-${d}`}
            label={DURATION_LABELS[d]}
            onSelect={() => onNext(d)}
          />
        ))}
      </div>
    </div>
  );
}

function IntensityStep({ onNext }: { onNext: (v: CampaignIntensity) => void }) {
  return (
    <div data-testid="commander-step-intensity">
      <Question>How aggressively would you like to market?</Question>
      <div className="grid gap-3 sm:grid-cols-2">
        {(Object.keys(INTENSITY_LABELS) as CampaignIntensity[]).map((i) => {
          const meta = INTENSITY_LABELS[i];
          return (
            <RadioCard
              key={i}
              testid={`commander-intensity-${i}`}
              label={meta.title}
              subtitle={meta.note}
              onSelect={() => onNext(i)}
            />
          );
        })}
      </div>
    </div>
  );
}

function ChannelsStep({
  suggested,
  onFinish,
}: {
  suggested: CommanderChannel[];
  onFinish: (v: CommanderChannel[]) => void;
}) {
  const [selected, setSelected] = useState<Set<CommanderChannel>>(new Set(suggested));
  const toggle = (c: CommanderChannel) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };
  const canFinish = selected.size >= 1 && selected.size <= 6;
  return (
    <div data-testid="commander-step-channels">
      <Question>Which channels should I use?</Question>
      <p className="text-sm text-muted-foreground mb-4">
        I've pre-selected the ones that fit your goal. Adjust if you'd like.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {(Object.keys(CHANNEL_LABELS) as CommanderChannel[]).map((c) => {
          const active = selected.has(c);
          return (
            <button
              key={c}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(c)}
              className={cn(
                "px-3 py-2 rounded-full border-2 text-sm font-medium transition-all",
                active
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/40",
              )}
              data-testid={`commander-channel-${c}`}
            >
              {CHANNEL_LABELS[c]}
            </button>
          );
        })}
      </div>
      <Button
        disabled={!canFinish}
        onClick={() => onFinish([...selected])}
        data-testid="commander-finish"
      >
        That's the plan
      </Button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Error banner — Patch 37.4.6                                           
// ══════════════════════════════════════════════════════════════════════

/** Founder-safe error surface for the Plan step. Renders a friendly
 *  inline banner explaining what went wrong, and — when the backend
 *  code identifies a specific remediation — offers a one-tap CTA that
 *  routes there. All other error codes get a generic "Please try
 *  again" with a Dismiss button. */
function CommanderErrorBanner({
  error,
  onDismiss,
  onManageCampaigns,
}: {
  error: { code: string; message: string; status: number };
  onDismiss: () => void;
  onManageCampaigns: () => void;
}) {
  const isCapExceeded = error.code === "active_campaign_cap_exceeded";
  return (
    <div
      role="alert"
      className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100/90"
      data-testid="commander-error-banner"
      data-error-code={error.code}
    >
      <p className="font-semibold text-amber-100">
        {isCapExceeded ? "You've hit your campaign limit." : "We couldn't plan that campaign."}
      </p>
      <p className="mt-1 text-xs text-amber-100/80" data-testid="commander-error-message">
        {error.message}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {isCapExceeded ? (
          <Button
            type="button"
            size="sm"
            onClick={onManageCampaigns}
            data-testid="commander-manage-campaigns"
          >
            Manage active campaigns
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDismiss}
          data-testid="commander-error-dismiss"
          className="text-amber-100/80 hover:text-amber-50"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Reusable radio card                                                   
// ══════════════════════════════════════════════════════════════════════

function RadioCard({
  label,
  subtitle,
  onSelect,
  testid,
}: {
  label: string;
  subtitle?: string;
  onSelect: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      className="text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
    >
      <Card className="p-4 border-2 border-border hover:border-primary/60 transition-all">
        <p className="font-medium">{label}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>
        )}
      </Card>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Answer summary                                                        
// ══════════════════════════════════════════════════════════════════════

function AnswerSummary({ answers }: { answers: Partial<CommanderAnswers> }) {
  const items: [string, string | undefined][] = [
    ["Promoting", answers.promotion],
    ["Audience", answers.target_audience],
    ["Goal", answers.business_goal ? GOAL_LABELS[answers.business_goal] : undefined],
    ["Duration", answers.duration ? DURATION_LABELS[answers.duration] : undefined],
    ["Intensity", answers.intensity ? INTENSITY_LABELS[answers.intensity].title : undefined],
  ];
  const filled = items.filter(([, v]) => Boolean(v));
  if (filled.length === 0) return null;
  return (
    <ul className="space-y-1.5 mb-6 text-sm text-muted-foreground">
      {filled.map(([k, v]) => (
        <li key={k} className="flex gap-2">
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
          <span className="font-medium text-foreground">{k}:</span>
          <span className="truncate">{v}</span>
        </li>
      ))}
    </ul>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Promise screen — Product Principles Article III                       
// ══════════════════════════════════════════════════════════════════════

function PromiseScreen({
  response,
  pending,
}: {
  response: CommanderResponse | null;
  pending: boolean;
}) {
  const navigate = useNavigate();
  const promise =
    response?.promise ??
    "Excellent. I understand your business objective. I'm now preparing a complete marketing strategy for your review.";

  // Auto-transition to the Marketing Strategy view once the draft is
  // saved. The Promise sentence lingers for ~1.5s so the owner has
  // time to feel it before the strategy composes.
  const nextUrl = response?.next_step_url;
  const navRef = useRef<number | null>(null);
  if (nextUrl && navRef.current === null) {
    navRef.current = window.setTimeout(() => {
      navigate(nextUrl);
    }, 1500);
  }

  return (
    <div
      className="mx-auto max-w-2xl px-4 pt-24 pb-24 text-center"
      data-testid="commander-promise-screen"
    >
      <div className="flex items-center justify-center gap-2 text-muted-foreground mb-6">
        {pending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span className="text-sm">Working…</span>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden />
            <span className="text-sm">Received.</span>
          </>
        )}
      </div>
      <blockquote
        className="text-2xl sm:text-3xl font-serif leading-snug tracking-tight text-foreground"
        data-testid="commander-promise-sentence"
      >
        &ldquo;{promise}&rdquo;
      </blockquote>
      {response?.blueprint?.id && (
        <p
          className="text-xs text-muted-foreground mt-8"
          data-testid="commander-blueprint-id"
        >
          Blueprint {response.blueprint.id}
        </p>
      )}
    </div>
  );
}
